import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  Search,
  ArrowLeft,
  ShieldCheck,
  Tag,
  AlertTriangle,
  Info,
  Check,
  ArrowRight,
} from "lucide-react";
import { track } from "../analytics";
import Seo, { SITE_URL, OG_IMAGE, breadcrumbLd, faqLd, organizationLd } from "../components/Seo";
import { CategoryChips } from "./Category";
import { AdUnit } from "../components/AdSense";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const CONCERN_LABEL = {
  low: "Low concern",
  moderate: "Moderate concern",
  "context-dependent": "Depends on context",
  "avoid-if-sensitive": "Avoid if sensitive",
};

const GlossaryHeader = () => (
  <header className="blog-top">
    <Link to="/" className="blog-brand">
      InZoRAH
    </Link>
    <nav aria-label="Site navigation">
      <Link
        to="/ingredients"
        onClick={() => track("click_ingredients_az", { label: "glossary header" })}
      >
        Ingredients A-Z
      </Link>
      <Link
        to="/ingredient"
        onClick={() => track("click_ingredient_blog", { label: "glossary header" })}
      >
        Ingredient blog
      </Link>
    </nav>
  </header>
);

const CONCERN_COPY = {
  low: "Widely regarded as safe at the levels used in products.",
  moderate: "Worth a second look — the dose and how often you use it matter.",
  "context-dependent": "Whether it matters depends on you and on the product.",
  "avoid-if-sensitive": "Skip it if you are sensitive, allergic or reacting.",
};

const ConcernPill = ({ level }) => (
  <span className={`gl-pill lvl-${(level || "").replace(/\s/g, "-")}`}>
    {CONCERN_LABEL[level] || level}
  </span>
);

const VerdictBanner = ({ level }) => {
  const key = (level || "").replace(/\s/g, "-");
  const Icon = key === "low" ? Check : key === "avoid-if-sensitive" ? AlertTriangle : Info;
  return (
    <div className={`gl-verdict lvl-${key}`} data-testid="ingredient-verdict">
      <span className="gl-verdict-icon">
        <Icon aria-hidden="true" />
      </span>
      <span>
        <b>{CONCERN_LABEL[level] || level}</b>
        <span>{CONCERN_COPY[level] || "We show the evidence and let you decide."}</span>
      </span>
    </div>
  );
};

/* ------------------------------------------------ index ------------- */
export const GlossaryIndex = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [letter, setLetter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/glossary`, { params: { limit: 1000 } })
      .then((r) => {
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      if (letter && i.letter !== letter) return false;
      if (!q) return true;
      const hay = `${i.name} ${(i.aliases || []).join(" ")} ${i.category}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, letter]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((i) => {
      const l = i.letter || "#";
      map[l] = map[l] || [];
      map[l].push(i);
    });
    return Object.keys(map)
      .sort()
      .map((l) => ({ letter: l, entries: map[l] }));
  }, [filtered]);

  const activeLetters = useMemo(
    () => new Set(items.map((i) => i.letter)),
    [items]
  );

  // record what people search for (debounced, only meaningful terms)
  useEffect(() => {
    const term = query.trim();
    if (term.length < 3) return;
    const id = setTimeout(() => track("glossary_search", { label: term }), 900);
    return () => clearTimeout(id);
  }, [query]);

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: "InZoRAH ingredient glossary",
    url: `${SITE_URL}/ingredients`,
    description:
      "An A-Z glossary of single food, drink, supplement and skincare ingredients, each explained in plain English with public sources.",
    inLanguage: "en",
    hasDefinedTerm: items.slice(0, 100).map((i) => ({
      "@type": "DefinedTerm",
      name: i.name,
      description: i.one_liner,
      url: `${SITE_URL}/ingredients/${i.slug}`,
    })),
  };

  return (
    <>
      <Seo
        title="Ingredient Checker A-Z: Is This Ingredient Safe? | InZoRAH"
        description="An A-Z ingredient checker: what each ingredient is, what it does, who should care and what the public record says about whether it is safe. Plain English, sources named, no medical advice."
        path="/ingredients"
        keywords="ingredient checker, ingredient analyzer, is this ingredient safe, food ingredient scanner, ingredient glossary, ingredient dictionary, food additive list, e numbers, skincare ingredient meanings"
        jsonLd={[
          itemListLd,
          organizationLd,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Ingredient glossary", path: "/ingredients" },
          ]),
        ]}
      />
      <div className="blog-shell">
        <GlossaryHeader />
        <main className="blog-main">
          <p className="blog-eyebrow">Ingredient glossary</p>
          <h1 className="blog-h1">What ingredients are, one at a time — A to Z.</h1>
          <p className="blog-intro">
            Use this as a plain-English ingredient checker: look up a single ingredient and
            get the short version — what it is, what it does in the product, how it is
            regulated, whether it is safe for you specifically, and what a lower-concern
            alternative looks like. Every entry is written from public records and names its
            sources. Educational information, not medical advice.
          </p>

          {!loading && total === 0 && (
            <div className="gl-empty">
              <h2>The glossary is filling up right now</h2>
              <p>
                Entries are written one at a time and published as soon as they are ready —
                the first batch usually appears within a few minutes of launch, and new
                ingredients are added every day after that. Meanwhile, the{" "}
                <Link to="/ingredient">ingredient blog</Link> already has full explainers.
              </p>
            </div>
          )}

          {total > 0 && (
            <div className="gl-tools">
            <label className="gl-search">
              <Search size={16} aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search an ingredient or label name…"
                aria-label="Search ingredients"
              />
            </label>
            <div className="gl-alpha" role="group" aria-label="Jump to letter">
              <button
                type="button"
                className={`gl-letter ${letter === "" ? "is-on" : ""}`}
                onClick={() => setLetter("")}
              >
                All
              </button>
              {ALPHABET.map((l) => (
                <button
                  key={l}
                  type="button"
                  disabled={!activeLetters.has(l)}
                  title={
                    activeLetters.has(l)
                      ? `Ingredients starting with ${l}`
                      : `No ingredients starting with ${l} yet`
                  }
                  className={`gl-letter ${letter === l ? "is-on" : ""}`}
                  onClick={() => setLetter(l === letter ? "" : l)}
                >
                  {l}
                </button>
              ))}
            </div>
            <p className="gl-showing" aria-live="polite">
              {letter || query
                ? `Showing ${filtered.length} of ${total} ingredients${
                    letter ? ` starting with ${letter}` : ""
                  }`
                : `Showing all ${total} ingredients`}
              {(letter || query) && (
                <button
                  type="button"
                  className="gl-clear"
                  onClick={() => {
                    setLetter("");
                    setQuery("");
                  }}
                >
                  Clear
                </button>
              )}
            </p>
            </div>
          )}

          <CategoryChips />

          {loading && <p className="blog-state">Loading the glossary…</p>}
          {!loading && total > 0 && filtered.length === 0 && (
            <p className="blog-state">
              No ingredient matches that yet. New entries are added every day.
            </p>
          )}

          {grouped.map((group, gi) => (
            <React.Fragment key={group.letter}>
              <section className="gl-group">
                <h2 className="gl-group-h">{group.letter}</h2>
                <div className="gl-grid">
                {group.entries.map((i) => (
                  <article className={`gl-card lvl-${(i.concern_level || "").replace(/\s/g, "-")}`} key={i.slug}>
                    <Link to={`/ingredients/${i.slug}`}>
                      <h3>{i.name}</h3>
                      <p>{i.one_liner}</p>
                      <div className="gl-card-foot">
                        <span className="gl-cat">
                          <Tag size={11} aria-hidden="true" /> {i.category}
                        </span>
                        <ConcernPill level={i.concern_level} />
                      </div>
                    </Link>
                  </article>
                ))}
                </div>
              </section>
              {gi === 0 && <AdUnit variant="display" />}
            </React.Fragment>
          ))}

          {total > 0 && (
            <p className="blog-count">
              {total} ingredient {total === 1 ? "entry" : "entries"} and growing — new ones
              are added daily.
            </p>
          )}

          <section className="blog-cta">
            <h2>Do this from the aisle instead</h2>
            <p>
              InZoRAH scans the whole ingredient list at once and tells you what ingredients
              are inside, with sources, then finds a cleaner product on the same shelf.
            </p>
            <Link
              to="/ingredient"
              className="blog-cta-btn"
              onClick={() => track("click_ingredient_blog", { label: "glossary cta" })}
            >
              Read the ingredient blog
            </Link>
          </section>
        </main>
        <footer className="blog-foot">
          KYSolutions LLC · <Link to="/ingredient">Ingredient blog</Link> ·{" "}
          <Link to="/contact">Contact</Link> ·{" "}
          <a
            className="inz-support-link"
            href="https://royalelite.support"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-portal-link"
          >
            Support
          </a>{" "}
          ·{" "}
          
          <Link to="/legal/privacy">Privacy</Link> · <Link to="/">Home</Link> ·{" "}
          <Link to="/admin/login" rel="nofollow">
            Admin
          </Link>
        </footer>
      </div>
    </>
  );
};

/* ------------------------------------------------ entry ------------- */
export const GlossaryEntry = () => {
  const { slug } = useParams();
  const [entry, setEntry] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    axios
      .get(`${API}/glossary/${slug}`)
      .then((r) => {
        setEntry(r.data?.entry || null);
        setRelated(r.data?.related || []);
        setStatus("ok");
        track("view_glossary_entry", { label: r.data?.entry?.name || slug });
      })
      .catch(() => setStatus("missing"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="blog-shell">
        <GlossaryHeader />
        <main className="blog-main">
          <p className="blog-state">Loading…</p>
        </main>
      </div>
    );
  }

  if (status === "missing" || !entry) {
    return (
      <div className="blog-shell">
        <Seo
          title="Ingredient not found | InZoRAH glossary"
          description="This ingredient entry could not be found."
          path={`/ingredients/${slug}`}
          noindex
        />
        <GlossaryHeader />
        <main className="blog-main">
          <h1 className="blog-h1">Ingredient not found</h1>
          <p className="blog-intro">
            We have not written that one up yet.{" "}
            <Link to="/ingredients">Browse the A-Z glossary</Link>.
          </p>
        </main>
      </div>
    );
  }

  const termLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: entry.name,
    alternateName: entry.aliases || [],
    description: entry.definition,
    url: `${SITE_URL}/ingredients/${entry.slug}`,
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      name: "InZoRAH ingredient glossary",
      url: `${SITE_URL}/ingredients`,
    },
  };

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${entry.name}: what this ingredient is and what it does`,
    description: entry.meta_description,
    image: OG_IMAGE,
    datePublished: entry.published_at,
    dateModified: entry.published_at,
    inLanguage: "en",
    author: { "@type": "Organization", name: "InZoRAH" },
    publisher: { "@type": "Organization", name: "KYSolutions LLC" },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/ingredients/${entry.slug}`,
    },
  };

  return (
    <>
      <Seo
        title={`${entry.meta_title || entry.name} | InZoRAH`}
        description={entry.meta_description}
        path={`/ingredients/${entry.slug}`}
        type="article"
        publishedTime={entry.published_at}
        keywords={[
          `is ${entry.name} safe`,
          `what is ${entry.name}`,
          entry.name,
          ...(entry.aliases || []),
          entry.category,
          "ingredient checker",
        ].join(", ")}
        jsonLd={[
          termLd,
          articleLd,
          entry.faq && entry.faq.length ? faqLd(entry.faq) : null,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Ingredient glossary", path: "/ingredients" },
            { name: entry.name, path: `/ingredients/${entry.slug}` },
          ]),
        ].filter(Boolean)}
      />
      <div className="blog-shell">
        <GlossaryHeader />
        <main className="blog-main blog-article">
          <nav className="blog-crumbs" aria-label="Breadcrumb">
            <Link to="/ingredients">
              <ArrowLeft size={13} aria-hidden="true" /> All ingredients A-Z
            </Link>
          </nav>

          <h1 className="post-h1">{entry.name}</h1>
          <div className="gl-badges">
            <span className="gl-cat">
              <Tag size={12} aria-hidden="true" /> {entry.category}
            </span>
            <ConcernPill level={entry.concern_level} />
          </div>

          <VerdictBanner level={entry.concern_level} />

          {(entry.aliases || []).length > 0 && (
            <p className="gl-aliases">
              <strong>Also on labels as:</strong> {entry.aliases.join(", ")}
            </p>
          )}

          <div className="post-answer">
            <h2>In one line</h2>
            <p>{entry.one_liner}</p>
          </div>

          <div className="post-body">
            <section>
              <h2>What is {entry.name}?</h2>
              <p>{entry.definition}</p>
            </section>

            <AdUnit variant="in-article" />

            <section>
              <h2>What does it do in the product?</h2>
              <p>{entry.what_it_does}</p>
              {(entry.found_in || []).length > 0 && (
                <>
                  <h3 className="gl-h3">Where you usually find it</h3>
                  <ul>
                    {entry.found_in.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </>
              )}
            </section>

            <section>
              <h2>Is {entry.name} safe, or something to worry about?</h2>
              <p>{entry.concern_summary}</p>
            </section>

            {(entry.who_should_care || []).length > 0 && (
              <section>
                <h2>Who should pay attention</h2>
                <ul className="gl-care">
                  {entry.who_should_care.map((w) => (
                    <li key={w}>
                      <AlertTriangle size={13} aria-hidden="true" /> {w}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section>
              <h2>How is it regulated?</h2>
              <p>{entry.regulatory}</p>
            </section>

            <section>
              <h2>How to spot it on a label</h2>
              <p>{entry.reading_the_label}</p>
            </section>

            <AdUnit variant="in-feed" />

            <section>
              <h2>What a cleaner swap looks like</h2>
              <p>{entry.cleaner_swap}</p>
            </section>
          </div>

          {(entry.faq || []).length > 0 && (
            <section className="post-faq">
              <h2>Questions people also ask about {entry.name}</h2>
              {entry.faq.map((f) => (
                <div key={f.question}>
                  <h3>{f.question}</h3>
                  <p>{f.answer}</p>
                </div>
              ))}
            </section>
          )}

          {(entry.sources || []).length > 0 && (
            <section className="post-sources">
              <h2>Where this came from</h2>
              <ul>
                {entry.sources.map((s) => (
                  <li key={s.name}>
                    <ShieldCheck size={13} aria-hidden="true" />
                    <span>
                      <b>{s.name}</b>
                      {s.note ? ` — ${s.note}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="post-disclaimer">
                <Info size={12} aria-hidden="true" /> Original write-up based on public
                records. Educational information, not medical advice — ingredient records
                change, so always read the physical label if you have an allergy.
              </p>
            </section>
          )}

          <section className="blog-cta">
            <h2>Scan the whole list instead</h2>
            <p>
              InZoRAH reads every ingredient on the pack at once and tells you which ones
              matter for you, with sources.
            </p>
            <Link
              to="/ingredient"
              className="blog-cta-btn"
              onClick={() => track("click_ingredient_blog", { label: "glossary cta" })}
            >
              Read the ingredient blog
            </Link>
          </section>

          {related.length > 0 && (
            <section className="post-related">
              <h2>Related ingredients</h2>
              <div className="gl-grid">
                {related.map((r) => (
                  <article className="gl-card" key={r.slug}>
                    <Link to={`/ingredients/${r.slug}`}>
                      <h3>{r.name}</h3>
                      <p>{r.one_liner}</p>
                      <span className="blog-read">
                        Read entry <ArrowRight size={13} aria-hidden="true" />
                      </span>
                    </Link>
                  </article>
                ))}
              </div>
            </section>
          )}
        </main>
        <footer className="blog-foot">
          KYSolutions LLC · <Link to="/ingredient">Ingredient blog</Link> ·{" "}
          <Link to="/contact">Contact</Link> ·{" "}
          <a
            className="inz-support-link"
            href="https://royalelite.support"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-portal-link"
          >
            Support
          </a>{" "}
          ·{" "}
          
          <Link to="/legal/privacy">Privacy</Link> · <Link to="/">Home</Link> ·{" "}
          <Link to="/admin/login" rel="nofollow">
            Admin
          </Link>
        </footer>
      </div>
    </>
  );
};
